/*
  assets/la-shipping.js
  Shared shipping date engine for Leg Avenue (Pacific time, Mon to Fri business days)
  plus the renderer for snippets/pdp-shipping-line.liquid.

  Public API (window.LAShipping):
    schedule(cfg, nowMs)  -> ship date, seconds to cutoff, and an arrival window per method
    init()                -> mounts every [data-la-pdp] on the page (safe to call again)
    setPrice(cents)       -> optional hook for themes that change variants without a form input
*/
(function () {
  'use strict';
  if (window.LAShipping) return;

  var TZ = 'America/Los_Angeles';
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, hourCycle: 'h23',
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric'
  });
  var clockOffset = 0;
  var overridePrice = null;

  /* ---------- date helpers ---------- */

  function ptParts(ms) {
    var o = {};
    fmt.formatToParts(new Date(ms)).forEach(function (p) {
      if (p.type !== 'literal') o[p.type] = parseInt(p.value, 10);
    });
    return { y: o.year, m: o.month, d: o.day, h: o.hour, mi: o.minute, s: o.second };
  }

  // Absolute time (ms) of a Pacific wall-clock hour on a given date.
  function ptToMs(y, m, d, h) {
    var guess = Date.UTC(y, m - 1, d, h, 0, 0);
    var p = ptParts(guess);
    var res = guess - (Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - guess);
    var p2 = ptParts(res);
    return guess - (Date.UTC(p2.y, p2.m - 1, p2.d, p2.h, p2.mi, p2.s) - res);
  }

  function key(dt) { return dt.y * 10000 + dt.m * 100 + dt.d; }
  function addDays(dt, n) {
    var t = new Date(Date.UTC(dt.y, dt.m - 1, dt.d + n));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
  }
  function dow(dt) { return new Date(Date.UTC(dt.y, dt.m - 1, dt.d)).getUTCDay(); }
  function parseKey(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim());
    return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
  }
  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function md(dt) { return MONTHS[dt.m - 1] + ' ' + dt.d; }
  function mdDay(dt) { return DAYS_SHORT[dow(dt)] + ', ' + md(dt); }

  // "Oct 22 to 26", "Oct 30 to Nov 2", or "Oct 21"
  function fmtShort(a, b) {
    if (key(a) === key(b)) return md(a);
    if (a.m === b.m) return md(a) + ' to ' + b.d;
    return md(a) + ' to ' + md(b);
  }
  // Single dates get a weekday ("Wed, Oct 21"), ranges do not.
  function fmtLong(a, b) {
    return key(a) === key(b) ? mdDay(a) : fmtShort(a, b);
  }
  function fmtTime(s) {
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);
    if (d > 0) return d + 'd ' + h + 'h';
    if (h > 0) return h + 'h ' + pad(m) + 'm';
    if (m > 0) return m + 'm';
    return 'less than 1m';
  }
  function fill(t, v) {
    return String(t || '').replace(/\{(\w+)\}/g, function (_, k) { return v[k] != null ? v[k] : ''; });
  }

  function fmtMoney(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d : d.toFixed(2));
  }

  /* ---------- the engine ---------- */

  // cfg: { cutoff, deadline, hideAfter, skip, methods: [{ id, min, max }] }
  function schedule(cfg, nowMs) {
    var cutoff = typeof cfg.cutoff === 'number' ? cfg.cutoff : 12;
    var deadline = parseKey(cfg.deadline);
    var hideAfter = parseKey(cfg.hideAfter);
    var skip = {};
    String(cfg.skip || '').split(/[\s,]+/).forEach(function (s) {
      var p = parseKey(s);
      if (p) skip[key(p)] = true;
    });

    function isBiz(dt) { var w = dow(dt); return w > 0 && w < 6 && !skip[key(dt)]; }
    function nextBiz(dt) { var n = addDays(dt, 1); while (!isBiz(n)) n = addDays(n, 1); return n; }
    function addBiz(dt, n) { var r = dt; while (n > 0) { r = nextBiz(r); n--; } return r; }

    var p = ptParts(nowMs);
    var today = { y: p.y, m: p.m, d: p.d };
    if (hideAfter && key(today) >= key(hideAfter)) return { hidden: true };

    var secs = p.h * 3600 + p.mi * 60 + p.s;
    var ship = (isBiz(today) && secs < cutoff * 3600) ? today : nextBiz(today);
    var target = ptToMs(ship.y, ship.m, ship.d, cutoff);

    return {
      hidden: false,
      ship: ship,
      shipIsToday: key(ship) === key(today),
      secsLeft: Math.max(0, Math.floor((target - nowMs) / 1000)),
      nextShip: DAYS[dow(ship)] + ', ' + md(ship),
      methods: (cfg.methods || []).map(function (m) {
        var min = m.min || 0;
        var max = m.max || min;
        if (max < min) max = min;
        var from = addBiz(ship, min);
        var to = addBiz(ship, max);
        var onTime = !deadline || key(to) <= key(deadline);
        // visible = arrives by the last delivery date. late + showLate = still offered, but after it.
        return { id: m.id, from: from, to: to, visible: onTime, late: !onTime, showLate: !!m.showLate };
      })
    };
  }

  function syncClock() {
    try {
      fetch('/cart.js', { method: 'HEAD', cache: 'no-store', credentials: 'same-origin' })
        .then(function (r) {
          var d = r.headers.get('date');
          var t = d ? Date.parse(d) : NaN;
          if (!isNaN(t)) clockOffset = t - Date.now();
        })
        .catch(function () {});
    } catch (e) {}
  }

  /* ---------- PDP renderer ---------- */

  function mountPdp(root) {
    if (root._laMounted) return;
    root._laMounted = true;

    var cfg = {};
    try { cfg = JSON.parse(root.querySelector('[data-la-config]').textContent); } catch (e) { return; }
    var copy = cfg.copy || {};
    var prices = cfg.prices || {};

    var head = root.querySelector('[data-la-head]');
    var sub = root.querySelector('[data-la-sub]');
    var warn = root.querySelector('[data-la-warn]');
    var toggle = root.querySelector('[data-la-toggle]');
    var panel = root.querySelector('[data-la-panel]');
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-la-row]'));
    var lastKey = '';
    var cartCents = 0;

    // cfg.cartBasis: "original" = subtotal before discounts (default), "discounted" = after line discounts.
    function refreshCart() {
      try {
        fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
          .then(function (r) { return r.json(); })
          .then(function (c) {
            var v = cfg.cartBasis === 'discounted' ? c.items_subtotal_price : c.original_total_price;
            cartCents = typeof v === 'number' ? v : 0;
            lastKey = '';
          })
          .catch(function () {});
      } catch (e) {}
    }

    // Price (cents) and availability of the variant currently selected in the product form.
    function currentVariant() {
      var input = document.querySelector(cfg.variantInput || 'form[action*="/cart/add"] [name="id"]');
      var v = input && cfg.variants ? cfg.variants[input.value] : null;
      return {
        p: overridePrice != null ? overridePrice : (v ? v.p : (cfg.price || 0)),
        a: v ? v.a : cfg.available !== false
      };
    }

    function setOpen(open) {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = open ? (copy.fewer || 'Fewer options') : (copy.more || 'More options');
      root.classList.toggle('lp--open', open);
    }

    function render() {
      var s = schedule(cfg, Date.now() + clockOffset);
      if (s.hidden) { root.hidden = true; return; }

      var vis = {};
      s.methods.forEach(function (m) { vis[m.id] = m; });
      var S = vis.standard && vis.standard.visible;
      var E = vis.expedited && vis.expedited.visible;
      var P = vis.priority && vis.priority.visible;
      // Standard offered but too slow for Halloween: listed in the panel with a clear warning.
      var SL = !!(vis.standard && vis.standard.late && vis.standard.showLate);
      var EL = !!(vis.expedited && vis.expedited.late && vis.expedited.showLate);
      function listed(m) { return !!(m && (m.visible || (m.late && m.showLate))); }

      // Free 2-day is based on the order subtotal: what is already in the cart plus this item.
      var cv = currentVariant();
      if (!cv.a) { root.hidden = true; lastKey = ''; return; }
      var effective = cartCents + cv.p;
      var qualifies = effective >= cfg.freeExpedited;
      var state, h, b;

      // International markets have a single method and no upsell.
      if (vis.international) {
        if (vis.international.visible) {
          state = 'intl';
          h = copy.headIntl;
          b = copy.subIntl;
        } else {
          state = 'closed';
          h = copy.closedHead;
          b = copy.closedSub;
        }
      // Always lead with the Expedited date. Standard lives in "More options".
      } else if (S) {
        state = 'full';
        h = copy.headQualified;
        b = qualifies ? copy.subQualified : copy.subUpsell;
      } else if (E) {
        state = 'noStandard';
        h = copy.headNoStandard;
        b = qualifies ? copy.subQualified : copy.subUpsell;
      } else if (P) {
        state = 'last';
        h = copy.headLast;
        b = copy.subLast;
      } else {
        state = 'closed';
        h = copy.closedHead;
        b = copy.closedSub;
      }

      var v = {
        time: fmtTime(s.secsLeft),
        nextShip: s.nextShip,
        standardPrice: prices.standard || '',
        expeditedPrice: prices.expedited || '',
        priorityPrice: prices.priority || '',
        remaining: fmtMoney(Math.max(0, cfg.freeExpedited - effective))
      };
      s.methods.forEach(function (m) { v[m.id] = fmtLong(m.from, m.to); });

      var headText = fill(h, v);
      var subText = fill(b, v);

      var shown = 0;
      var rowText = rows.map(function (row) {
        var m = vis[row.getAttribute('data-la-row')];
        var ok = listed(m);
        if (ok) shown++;
        var date = ok ? fmtShort(m.from, m.to) : '';
        var detail = row.getAttribute('data-detail') || '';
        if (qualifies && row.getAttribute('data-detail-free') && !(m && m.late)) detail = row.getAttribute('data-detail-free');
        return (ok ? (m.late ? 'L' : '1') : '0') + date + detail;
      }).join('|');

      // Warning line under the subline. When only Priority can still make Halloween, say so directly.
      var warnKey = '';
      if (state === 'last' && (SL || EL)) warnKey = 'warnPriority';
      else if (SL && state !== 'closed' && state !== 'intl') warnKey = 'warnStandard';

      var k = [state, warnKey, headText, subText, rowText].join('||');
      if (k === lastKey) return;
      lastKey = k;

      var comma = headText.indexOf(',');
      head.textContent = '';
      if (headText.indexOf('Order in') === 0 && comma > -1) {
        var strong = document.createElement('strong');
        strong.textContent = headText.slice(0, comma + 1);
        head.appendChild(strong);
        head.appendChild(document.createTextNode(headText.slice(comma + 1)));
      } else {
        head.textContent = headText;
      }
      sub.textContent = subText;
      // The dark "last call" look (class lp--last) is switched off: the bar keeps the default pink.
      // To bring it back, use: root.classList.toggle('lp--last', state === 'last');
      root.classList.remove('lp--last');

      // Collapsed bar: one clear line when Standard will miss Halloween (the open panel says it per row).
      if (warn) {
        warn.textContent = warnKey ? fill(copy[warnKey], v) : '';
        warn.setAttribute('data-kind', warnKey);
        warn.hidden = !warnKey;
      }

      rows.forEach(function (row) {
        var m = vis[row.getAttribute('data-la-row')];
        var ok = listed(m);
        row.hidden = !ok;
        var lateEl = row.querySelector('[data-la-late]');
        if (lateEl) lateEl.hidden = !(ok && m.late);
        row.classList.toggle('lp__row--late', !!(ok && m.late));
        if (!ok) return;
        row.querySelector('[data-la-date]').textContent = fmtShort(m.from, m.to);
        var detail = row.getAttribute('data-detail') || '';
        if (qualifies && row.getAttribute('data-detail-free') && !m.late) detail = row.getAttribute('data-detail-free');
        row.querySelector('[data-la-detail]').textContent = detail;
      });

      // Options that arrive after Halloween go below the ones that make it. Original order is kept within each group.
      var fine = panel.querySelector('.lp__fine');
      rows.slice().sort(function (a, b) {
        var la = a.classList.contains('lp__row--late') ? 1 : 0;
        var lb = b.classList.contains('lp__row--late') ? 1 : 0;
        return (la - lb) || (rows.indexOf(a) - rows.indexOf(b));
      }).forEach(function (r) { panel.insertBefore(r, fine); });

      var canExpand = shown >= 2 && state !== 'closed';
      toggle.hidden = !canExpand;
      if (!canExpand) setOpen(false);

      root.hidden = false;
    }

    toggle.addEventListener('click', function () { setOpen(panel.hidden); });

    // Refresh the cart subtotal on load, after add-to-cart clicks, on tab focus, and every 30s.
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('form[action*="/cart/add"] button, form[action*="/cart/add"] [type="submit"]') : null;
      if (t) { setTimeout(refreshCart, 1500); setTimeout(refreshCart, 4000); }
    });
    document.addEventListener('visibilitychange', function () { if (!document.hidden) refreshCart(); });
    setInterval(refreshCart, 30000);

    refreshCart();
    render();
    root._laTimer = setInterval(render, 1000);
  }

  function init() {
    var roots = document.querySelectorAll('[data-la-pdp]');
    for (var i = 0; i < roots.length; i++) mountPdp(roots[i]);
  }

  window.LAShipping = {
    schedule: schedule,
    init: init,
    setPrice: function (cents) { overridePrice = typeof cents === 'number' ? cents : null; }
  };

  syncClock();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();